import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "../lib/api";
import { formatDateTime } from "../lib/utils";
import { Search, RefreshCw, Play, Mail, MessageCircle, Phone, X, Loader2, Building2, ExternalLink, Download } from "lucide-react";

interface HhLead {
  id: string;
  hhVacancyId: string;
  hhEmployerId: string;
  employerName: string;
  employerUrl: string | null;
  vacancyName: string;
  vacancyUrl: string | null;
  vacancyArea: string | null;
  vacancyAreaId: string | null;
  vacancyPublishedAt: string | null;
  searchQuery: string | null;
  inn: string | null;
  directorName: string | null;
  directorPosition: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactTelegram: string | null;
  status: string;
  outreachChannel: string | null;
  outreachMessage: string | null;
  outreachSentAt: string | null;
  notes: string | null;
  assignedTo: string | null;
  createdAt: string;
  updatedAt: string;
}

interface HhEmployer {
  hhEmployerId: string;
  employerName: string;
  employerUrl: string | null;
  vacancyCount: number;
  areas: string[] | null;
  status: string;
  inn: string | null;
  directorName: string | null;
  directorPosition: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactTelegram: string | null;
  notes: string | null;
  assignedTo: string | null;
  outreachChannel: string | null;
  outreachSentAt: string | null;
  firstSeenAt: string | null;
  latestLeadId: string;
}

interface HhScannerStats {
  total: number;
  byStatus: Record<string, number>;
  enrichedCount: number;
  contactedCount: number;
}

interface HhConfig {
  id: string;
  name: string;
  searchText: string;
  area: string | null;
  schedule: string;
  isActive: boolean;
  lastRunAt: string | null;
  lastRunCount: number | null;
  createdAt: string;
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  new: { label: "Новый", className: "bg-slate-100 text-slate-700" },
  enriched: { label: "Обогащён", className: "bg-blue-100 text-blue-700" },
  contacted: { label: "Связались", className: "bg-yellow-100 text-yellow-700" },
  demo: { label: "Демо", className: "bg-purple-100 text-purple-700" },
  negotiation: { label: "Переговоры", className: "bg-orange-100 text-orange-700" },
  client: { label: "Клиент", className: "bg-green-100 text-green-700" },
  rejected: { label: "Отказ", className: "bg-red-100 text-red-700" },
  skipped: { label: "Пропущен", className: "bg-gray-100 text-gray-500" },
};

function StatusBadge({ status }: { status: string }) {
  const item = STATUS_LABELS[status] || { label: status, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.className}`}>
      {item.label}
    </span>
  );
}

export default function HhScannerPage() {
  const [tab, setTab] = useState<"employers" | "configs">("employers");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [minVacancies, setMinVacancies] = useState("");
  const [selectedEmployer, setSelectedEmployer] = useState<HhEmployer | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: stats } = useQuery<HhScannerStats>({
    queryKey: ["/api/admin/hh-scanner/stats"],
  });

  const employersQueryKey = `/api/admin/hh-scanner/employers?${new URLSearchParams({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(search ? { search } : {}),
    ...(minVacancies ? { minVacancies } : {}),
    limit: "200",
  }).toString()}`;

  const { data: employers, isLoading: employersLoading } = useQuery<HhEmployer[]>({
    queryKey: [employersQueryKey],
    enabled: tab === "employers",
  });

  const bulkEnrichMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/hh-scanner/employers/enrich-batch", {
        employerIds: Array.from(selectedIds),
      });
      return res.json() as Promise<{ enriched: number; failed: number; skipped: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [employersQueryKey] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/stats"] });
      setSelectedIds(new Set());
      alert(
        `DaData: обогащено ${data.enriched}, пропущено (уже) ${data.skipped}, ошибок ${data.failed}`,
      );
    },
  });

  const bulkContactsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        "/api/admin/hh-scanner/employers/enrich-contacts-batch",
        { employerIds: Array.from(selectedIds) },
      );
      return res.json() as Promise<{ found: number; failed: number; skipped: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [employersQueryKey] });
      setSelectedIds(new Set());
      alert(
        `Контакты: найдено ${data.found}, пропущено (уже есть) ${data.skipped}, не найдено ${data.failed}`,
      );
    },
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = (employers ?? []).map((e) => e.hhEmployerId);
    if (selectedIds.size === visibleIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleIds));
    }
  };

  const allVisibleSelected =
    (employers?.length ?? 0) > 0 && selectedIds.size === (employers?.length ?? 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">HH Scanner — лидогенерация</h1>
        <div className="flex gap-2">
          <DownloadCsvButton queryString={new URLSearchParams({
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(search ? { search } : {}),
            ...(minVacancies ? { minVacancies } : {}),
          }).toString()} />
          <RunAllButton />
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Всего вакансий" value={stats.total} />
          <StatCard
            label="Обогащено"
            value={`${stats.enrichedCount} (${stats.total ? Math.round((stats.enrichedCount / stats.total) * 100) : 0}%)`}
          />
          <StatCard
            label="Связались"
            value={`${stats.contactedCount} (${stats.total ? Math.round((stats.contactedCount / stats.total) * 100) : 0}%)`}
          />
          <StatCard label="Клиенты" value={stats.byStatus.client ?? 0} />
        </div>
      )}

      <div className="border-b">
        <div className="flex gap-1">
          <TabButton active={tab === "employers"} onClick={() => setTab("employers")}>
            Работодатели
          </TabButton>
          <TabButton active={tab === "configs"} onClick={() => setTab("configs")}>
            Конфигурации
          </TabButton>
        </div>
      </div>

      {tab === "employers" && (
        <>
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[300px]">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Поиск по названию компании..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-md text-sm"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm"
            >
              <option value="">Все статусы</option>
              {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={minVacancies}
              onChange={(e) => setMinVacancies(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm"
            >
              <option value="">Все компании</option>
              <option value="3">3+ вакансий</option>
              <option value="10">10+ вакансий</option>
              <option value="25">25+ вакансий</option>
              <option value="50">50+ вакансий</option>
            </select>
          </div>

          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-4 py-2">
              <span className="text-sm text-blue-700">
                Выделено: <strong>{selectedIds.size}</strong> компаний
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => bulkEnrichMutation.mutate()}
                  disabled={bulkEnrichMutation.isPending || bulkContactsMutation.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                >
                  {bulkEnrichMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : null}
                  DaData (директор, ИНН) ({selectedIds.size})
                </button>
                <button
                  onClick={() => bulkContactsMutation.mutate()}
                  disabled={bulkEnrichMutation.isPending || bulkContactsMutation.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded disabled:opacity-50"
                >
                  {bulkContactsMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : null}
                  Контакты (email/phone) ({selectedIds.size})
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-muted"
                >
                  Снять выделение
                </button>
              </div>
            </div>
          )}

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      className="cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Компания</th>
                  <th className="px-4 py-3 text-left font-medium">Вакансий</th>
                  <th className="px-4 py-3 text-left font-medium">Города</th>
                  <th className="px-4 py-3 text-left font-medium">Директор</th>
                  <th className="px-4 py-3 text-left font-medium">Контакты</th>
                  <th className="px-4 py-3 text-left font-medium">Быстрый поиск</th>
                  <th className="px-4 py-3 text-left font-medium">Статус</th>
                </tr>
              </thead>
              <tbody>
                {employersLoading && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                      Загрузка...
                    </td>
                  </tr>
                )}
                {!employersLoading && (employers ?? []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                      Нет работодателей. Запустите скан через «Конфигурации».
                    </td>
                  </tr>
                )}
                {(employers ?? []).map((emp) => (
                  <tr
                    key={emp.hhEmployerId}
                    className="border-t hover:bg-muted/30"
                  >
                    <td
                      className="px-3 py-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(emp.hhEmployerId);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(emp.hhEmployerId)}
                        onChange={() => toggleSelect(emp.hhEmployerId)}
                        className="cursor-pointer"
                      />
                    </td>
                    <td
                      className="px-4 py-3 font-medium cursor-pointer"
                      onClick={() => setSelectedEmployer(emp)}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                        {emp.employerName}
                      </div>
                      {emp.inn && <div className="text-xs text-muted-foreground mt-0.5">ИНН {emp.inn}</div>}
                    </td>
                    <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedEmployer(emp)}>
                      <span className="inline-block px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-semibold">
                        {emp.vacancyCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground cursor-pointer" onClick={() => setSelectedEmployer(emp)}>
                      {(emp.areas ?? []).slice(0, 3).join(", ")}
                      {(emp.areas ?? []).length > 3 && ` +${emp.areas!.length - 3}`}
                    </td>
                    <td className="px-4 py-3 text-xs cursor-pointer" onClick={() => setSelectedEmployer(emp)}>
                      {emp.directorName ? (
                        <>
                          <div className="font-medium">{emp.directorName}</div>
                          <div className="text-muted-foreground">{emp.directorPosition}</div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs cursor-pointer" onClick={() => setSelectedEmployer(emp)}>
                      {emp.contactEmail && <div>✉ {emp.contactEmail}</div>}
                      {emp.contactPhone && <div>☎ {emp.contactPhone}</div>}
                      {emp.contactTelegram && <div>📱 @{emp.contactTelegram}</div>}
                      {!emp.contactEmail && !emp.contactPhone && !emp.contactTelegram && (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex flex-col gap-1">
                        {emp.inn && (
                          <a
                            href={`https://www.rusprofile.ru/search?query=${emp.inn}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Rusprofile →
                          </a>
                        )}
                        {emp.employerUrl && (
                          <a
                            href={emp.employerUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            HH →
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedEmployer(emp)}>
                      <StatusBadge status={emp.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "configs" && <ConfigsTab />}

      {selectedEmployer && (
        <EmployerDetailPanel
          employer={selectedEmployer}
          onClose={() => setSelectedEmployer(null)}
          employersQueryKey={employersQueryKey}
        />
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="border rounded-lg p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-primary text-primary" : "border-transparent text-muted-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function DownloadCsvButton({ queryString }: { queryString: string }) {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const res = await fetch(
        `/api/admin/hh-scanner/employers.csv${queryString ? `?${queryString}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hh-scanner-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-medium hover:bg-muted disabled:opacity-50"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      Скачать CSV
    </button>
  );
}

function RunAllButton() {
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/hh-scanner/run-all");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/configs"] });
    },
  });

  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
    >
      {mutation.isPending ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <RefreshCw className="w-4 h-4" />
      )}
      Запустить все сканы
    </button>
  );
}

function ConfigsTab() {
  const { data: configs, isLoading } = useQuery<HhConfig[]>({
    queryKey: ["/api/admin/hh-scanner/configs"],
  });

  const [showNew, setShowNew] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-medium">Конфигурации сканирования</h2>
        <button
          onClick={() => setShowNew(!showNew)}
          className="px-3 py-1.5 text-sm border rounded-md hover:bg-muted"
        >
          {showNew ? "Отмена" : "+ Новый конфиг"}
        </button>
      </div>

      {showNew && <NewConfigForm onDone={() => setShowNew(false)} />}

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Название</th>
              <th className="px-4 py-3 text-left font-medium">Запрос</th>
              <th className="px-4 py-3 text-left font-medium">Город</th>
              <th className="px-4 py-3 text-left font-medium">Расписание</th>
              <th className="px-4 py-3 text-left font-medium">Последний запуск</th>
              <th className="px-4 py-3 text-left font-medium">Активен</th>
              <th className="px-4 py-3 text-right font-medium">Действия</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-muted-foreground">
                  Загрузка...
                </td>
              </tr>
            )}
            {!isLoading && (configs ?? []).length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-muted-foreground">
                  Нет конфигураций. Создайте первую для запуска сканирования.
                </td>
              </tr>
            )}
            {(configs ?? []).map((config) => (
              <ConfigRow key={config.id} config={config} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConfigRow({ config }: { config: HhConfig }) {
  const runMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/admin/hh-scanner/configs/${config.id}/run`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/configs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/stats"] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/admin/hh-scanner/configs/${config.id}`, {
        isActive: !config.isActive,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/configs"] });
    },
  });

  return (
    <tr className="border-t">
      <td className="px-4 py-3 font-medium">{config.name}</td>
      <td className="px-4 py-3">{config.searchText}</td>
      <td className="px-4 py-3">{config.area ?? "все"}</td>
      <td className="px-4 py-3">{config.schedule}</td>
      <td className="px-4 py-3 text-xs">
        {config.lastRunAt ? (
          <>
            {formatDateTime(config.lastRunAt)}
            <div className="text-muted-foreground">+{config.lastRunCount ?? 0} новых</div>
          </>
        ) : (
          <span className="text-muted-foreground">не запускался</span>
        )}
      </td>
      <td className="px-4 py-3">
        <button onClick={() => toggleMutation.mutate()} disabled={toggleMutation.isPending}>
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-medium cursor-pointer ${
              config.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
            }`}
          >
            {config.isActive ? "активен" : "выключен"}
          </span>
        </button>
      </td>
      <td className="px-4 py-3 text-right">
        <button
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-muted disabled:opacity-50"
        >
          {runMutation.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Play className="w-3 h-3" />
          )}
          Запустить
        </button>
      </td>
    </tr>
  );
}

function NewConfigForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [area, setArea] = useState("");
  const [schedule, setSchedule] = useState("daily");

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/hh-scanner/configs", {
        name,
        searchText,
        area: area || null,
        schedule,
        isActive: true,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/configs"] });
      onDone();
    },
  });

  return (
    <div className="border rounded-lg p-4 bg-muted/20 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium">Название</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Курьеры Москва"
            className="w-full mt-1 px-3 py-2 border rounded text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-medium">Поисковый запрос</label>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="курьер"
            className="w-full mt-1 px-3 py-2 border rounded text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-medium">ID региона HH</label>
          <input
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="1 — Москва, 2 — СПб, пусто — все"
            className="w-full mt-1 px-3 py-2 border rounded text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-medium">Расписание</label>
          <select
            value={schedule}
            onChange={(e) => setSchedule(e.target.value)}
            className="w-full mt-1 px-3 py-2 border rounded text-sm"
          >
            <option value="daily">Ежедневно</option>
            <option value="weekly">Еженедельно</option>
            <option value="manual">Вручную</option>
          </select>
        </div>
      </div>
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending || !name || !searchText}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
      >
        {mutation.isPending ? "Создаём..." : "Создать конфиг"}
      </button>
    </div>
  );
}

function EmployerDetailPanel({
  employer,
  onClose,
  employersQueryKey,
}: {
  employer: HhEmployer;
  onClose: () => void;
  employersQueryKey: string;
}) {
  const [notes, setNotes] = useState(employer.notes ?? "");
  const [status, setStatus] = useState(employer.status);
  const [outreachChannel, setOutreachChannel] = useState<"email" | "telegram" | "phone">(
    (employer.outreachChannel as "email" | "telegram" | "phone" | null) || "telegram",
  );
  const [outreachText, setOutreachText] = useState<string>("");

  const { data: vacancies } = useQuery<HhLead[]>({
    queryKey: [`/api/admin/hh-scanner/employers/${employer.hhEmployerId}/leads`],
  });

  const enrichMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/admin/hh-scanner/leads/${employer.latestLeadId}/enrich`,
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [employersQueryKey] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/stats"] });
    },
  });

  const contactsEnrichMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/admin/hh-scanner/employers/${employer.hhEmployerId}/enrich-contacts`,
      );
      return res.json() as Promise<{ emailFound: boolean; phoneFound: boolean; telegramFound: boolean }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [employersQueryKey] });
      const found = [
        data.emailFound && "email",
        data.phoneFound && "телефон",
        data.telegramFound && "Telegram",
      ].filter(Boolean);
      alert(found.length > 0 ? `Найдено: ${found.join(", ")}` : "Контакты не найдены");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<HhEmployer>) => {
      const res = await apiRequest(
        "PATCH",
        `/api/admin/hh-scanner/employers/${employer.hhEmployerId}`,
        updates,
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [employersQueryKey] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/hh-scanner/stats"] });
    },
  });

  const outreachMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/admin/hh-scanner/leads/${employer.latestLeadId}/outreach`,
        { channel: outreachChannel },
      );
      return res.json();
    },
    onSuccess: (data: { body: string; subject?: string }) => {
      const text = data.subject ? `Тема: ${data.subject}\n\n${data.body}` : data.body;
      setOutreachText(text);
    },
  });

  const copyToClipboard = () => {
    if (outreachText) navigator.clipboard.writeText(outreachText);
  };

  const markContacted = () => {
    updateMutation.mutate({
      status: "contacted",
      outreachChannel,
      outreachSentAt: new Date().toISOString() as unknown as string,
    } as unknown as Partial<HhEmployer>);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={onClose}>
      <div
        className="w-[720px] h-full bg-background overflow-y-auto p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {employer.employerName}
            </h2>
            <div className="text-sm text-muted-foreground mt-1">
              {employer.vacancyCount} вакансий · {(employer.areas ?? []).join(", ")}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <InfoRow label="ИНН" value={employer.inn} />
          <InfoRow
            label="Первый раз найден"
            value={employer.firstSeenAt ? formatDateTime(employer.firstSeenAt) : null}
          />
          <InfoRow label="Директор" value={employer.directorName} />
          <InfoRow label="Должность" value={employer.directorPosition} />
          <InfoRow label="Email" value={employer.contactEmail} />
          <InfoRow label="Телефон" value={employer.contactPhone} />
          <InfoRow
            label="Telegram"
            value={employer.contactTelegram ? `@${employer.contactTelegram}` : null}
          />
          <InfoRow
            label="Аутрич отправлен"
            value={employer.outreachSentAt ? formatDateTime(employer.outreachSentAt) : null}
          />
        </div>

        <div className="flex gap-2 text-sm flex-wrap">
          {employer.employerUrl && (
            <a
              href={employer.employerUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 border rounded hover:bg-muted"
            >
              Компания на HH <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <button
            onClick={() => enrichMutation.mutate()}
            disabled={enrichMutation.isPending}
            className="px-3 py-1.5 border rounded hover:bg-muted disabled:opacity-50"
          >
            {enrichMutation.isPending ? "Обогащение..." : "DaData (директор)"}
          </button>
          <button
            onClick={() => contactsEnrichMutation.mutate()}
            disabled={contactsEnrichMutation.isPending}
            className="px-3 py-1.5 border rounded hover:bg-muted disabled:opacity-50"
          >
            {contactsEnrichMutation.isPending ? "Поиск..." : "Найти контакты (HH + сайт)"}
          </button>
        </div>

        <div>
          <label className="text-sm font-medium">Статус</label>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              updateMutation.mutate({ status: e.target.value });
            }}
            className="w-full mt-1 px-3 py-2 border rounded text-sm"
          >
            {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm font-medium">Заметки</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== (employer.notes ?? "")) updateMutation.mutate({ notes });
            }}
            rows={3}
            className="w-full mt-1 px-3 py-2 border rounded text-sm"
          />
        </div>

        <div className="border-t pt-4 space-y-3">
          <h3 className="font-medium">Сообщение для аутрича</h3>
          <div className="flex gap-2">
            <ChannelButton
              active={outreachChannel === "email"}
              onClick={() => setOutreachChannel("email")}
              icon={<Mail className="w-3 h-3" />}
              label="Email"
            />
            <ChannelButton
              active={outreachChannel === "telegram"}
              onClick={() => setOutreachChannel("telegram")}
              icon={<MessageCircle className="w-3 h-3" />}
              label="Telegram"
            />
            <ChannelButton
              active={outreachChannel === "phone"}
              onClick={() => setOutreachChannel("phone")}
              icon={<Phone className="w-3 h-3" />}
              label="Звонок"
            />
          </div>
          <button
            onClick={() => outreachMutation.mutate()}
            disabled={outreachMutation.isPending}
            className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-sm font-medium disabled:opacity-50"
          >
            {outreachMutation.isPending ? "Генерация..." : "Сгенерировать сообщение"}
          </button>
          {outreachText && (
            <>
              <textarea
                value={outreachText}
                onChange={(e) => setOutreachText(e.target.value)}
                rows={10}
                className="w-full px-3 py-2 border rounded text-sm font-mono"
              />
              <div className="flex gap-2">
                <button onClick={copyToClipboard} className="px-3 py-1.5 border rounded text-sm">
                  Скопировать
                </button>
                <button
                  onClick={markContacted}
                  className="px-3 py-1.5 bg-green-600 text-white rounded text-sm"
                >
                  Отметить как отправленное
                </button>
              </div>
            </>
          )}
        </div>

        <div className="border-t pt-4">
          <h3 className="font-medium mb-2">Вакансии ({employer.vacancyCount})</h3>
          <div className="border rounded-md divide-y">
            {(vacancies ?? []).slice(0, 20).map((v) => (
              <div key={v.id} className="px-3 py-2 text-xs flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{v.vacancyName}</div>
                  <div className="text-muted-foreground">{v.vacancyArea}</div>
                </div>
                {v.vacancyUrl && (
                  <a
                    href={v.vacancyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary shrink-0 ml-2"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
            {(vacancies ?? []).length > 20 && (
              <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                и ещё {(vacancies ?? []).length - 20}...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}

function ChannelButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border ${
        active ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
